import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import {
  DataService,
  PamlabApiServiceService,
  SnackbarService,
  AuthService,
  ScreenSizeService,
} from '@/shared/services';
import { Subscription } from 'rxjs';
import { Chart, ChartOptions, registerables } from 'chart.js';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import {
  HoursTrend,
  StudentTermByTerm,
  Semesters,
  RecommendedCourses,
  Combos,
  CourseInfoExtended,
  SupportedHoursTrendGroups,
  TermInfo,
  CourseInfoReturn,
  StudentReportedCourseReturn,
  RiskCommentInfo,
  AtRiskStudentReturn,
  SupportedHoursTrendTypes,
} from '../../../../../server/types';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { environment } from '../../../../../environments/environment';
import { ConfirmDialogComponent } from '@/shared/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AtRiskStudentPopupFormComponent } from '../../at-risk-student/at-risk-student-popup-form/at-risk-student-popup-form.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ProgramStudentChartComponent } from '../program-student-chart/program-student-chart.component';

@Component({
  selector: 'student-info-content',
  templateUrl: './student-info-content.component.html',
  styleUrls: ['./student-info-content.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
  standalone: false,
})
export class StudentInfoContentComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('filterInput') filterInput!: ElementRef;
  @ViewChild(ProgramStudentChartComponent)
  programStudentChart!: ProgramStudentChartComponent;

  public dataPending!: boolean;
  public env = environment;

  // save semester for dropdown table
  public filteredTakenCourse: number[] = [];

  public filteredCourseCode: string[] = [];

  public showAllTable: boolean = true;

  // variable to check for status of toggle button
  group = 'default';

  private subscription: Subscription = new Subscription();

  //columns definition for taken courses table
  displayedColumnsTaken: string[] = ['code', 'name', 'hours', 'grade'];

  //columns definition for registered courses table
  displayedColumnsRegistered: string[] = [
    'code',
    'name',
    'hours',
    'pass_rate',
    'grade_awarded',
    'grade_prediction',
    'rank',
    'delivery_method',
    'action',
  ];

  //columns definition for recommended courses table
  displayedColumnsRecommended: string[] = ['code', 'name', 'pass_rate'];

  //columns definition for course combos table
  displayedColumnsCourseCombo: string[] = ['courses', 'pass_rate'];

  //student data from data service
  studentData!: StudentTermByTerm;

  // Variable for data source for course combo table
  courseCombos!: Combos[];

  recommendedCoursesData!: RecommendedCourses;

  // Variable for data source for table
  recommendedCourses!: CourseInfoExtended[];

  // recommended course type desription variable
  type_desc: string = 'Mix of Easy/Popular';

  // recommended course type variable
  type: string = 'Good';

  // race of the student
  raceData: string = '';

  // gender of the student
  genderData: string = '';

  courseComboDataSource: any;

  // demo student term gpa data
  studentTermsGPA!: Semesters[];

  studentsTermGPAs!: TermInfo[];

  studentTermPrograms!: TermInfo[];

  hoursTrendData!: Partial<HoursTrend>;
  supportedHrsTrendData!: SupportedHoursTrendGroups;

  financialAidGiven: string[] = [];

  // financial aid chart variable declarations
  financialAidChart!: Chart;

  gpaComparisonChart!: Chart;

  // Expanded Element for registered courses table
  expandedElement!: CourseInfoReturn | null;

  // labels for inner table for registered courses table
  innerDisplayedColumns: string[] = [
    'course_section',
    'demo_time_frame',
    'enrollment_state',
    'grade_score',
    'grade_status',
    'last_interaction_time',
    'interacted_with_course',
    'week',
  ];

  riskCommmentDisplayedColumns: string[] = [
    'comment',
    'categories',
    'made_by',
    'createdAt',
    'action',
  ];

  // Variable for registered courses
  registeredCoursesData!: CourseInfoReturn[];

  // data source variable for canvas report data for inner table
  canvasReportsDataSource: Record<number, MatTableDataSource<any>> = {};
  atRiskCommentDataSource: Record<number, MatTableDataSource<RiskCommentInfo>> =
    {};

  takenCoursesData: Array<CourseInfoReturn[]> = new Array();

  panelOpenState: boolean = true;
  isAtRisk: boolean = false;
  new_demo_time_frame = '';
  hoursChart!: Chart;
  numCardCols: number = 6;
  cardsContainerWidth!: number;
  contentWidth!: number;
  numInfoCards: number = 6;

  constructor(
    public dataService: DataService,
    private apiService: PamlabApiServiceService,
    private _dialog: MatDialog,
    public authService: AuthService,
    private snackBarService: SnackbarService,
    private screenSizeService: ScreenSizeService,
  ) {}

  ngOnInit(): void {
    if (!this.authService.hasPermission('at-risk-students', true)) {
      this.riskCommmentDisplayedColumns =
        this.riskCommmentDisplayedColumns.filter((x) => x !== 'action');
    }

    this.subscription.add(
      this.dataService.getData().subscribe((dataStream) => {
        // if error message is present then display error message
        if (dataStream.error) {
          console.log(dataStream.error);
          this.snackBarService.open(dataStream.error.message, 'error');
        }

        if (dataStream.data) {
          this.studentData = dataStream.data as StudentTermByTerm;
          this.studentData.at_risk_data.forEach((x) => {
            this.atRiskCommentDataSource[x.id] = new MatTableDataSource(
              x.risk_comments,
            );
          });

          this.isAtRisk =
            this.studentData.at_risk_data.findIndex(
              (x) =>
                x.demo_time_frame === this.dataService.getCurrentSemester(),
            ) !== -1;

          // getting student registered courses for table
          this.registeredCoursesData = this.studentData.registered_courses;

          // getting student course combos for table
          this.courseCombos = this.studentData.combos!;
          this.courseComboDataSource = new MatTableDataSource<Combos>(
            this.courseCombos,
          );

          // getting course recommender year for recommendation
          this.getCourseRecommenderDemoTimeFrame();

          // getting student recommended courses fr table
          this.recommendedCoursesData = this.studentData.recommended_courses!;
          this.recommendedCourses = this.recommendedCoursesData.good!;

          this.studentsTermGPAs = this.studentData.uwf_gpas;
          this.studentTermPrograms = this.studentData.program_cip_descs;
          this.financialAidCheck(this.studentData);

          this.hoursTrendData =
            this.studentData.admit_type === 'FTIC'
              ? this.studentData.hours_trend!
              : this.removeIdealHours(this.studentData.hours_trend!);

          this.supportedHrsTrendData = this.studentData.supported_hours_trend!;

          (this.studentData.admit_type === 'FTIC' ||
            this.studentData.admit_type === 'FCSAA') &&
            this.generateChartData();

          setTimeout(
            () => (this.courseComboDataSource.paginator = this.paginator),
          );
        }
      }),
    );

    this.subscription.add(
      this.dataService.getAtRiskStudentData().subscribe({
        next: (dataStream) => {
          if (dataStream.data) {
            const data = dataStream.data as AtRiskStudentReturn;
            this.studentData.at_risk_data.push(data);
            this.atRiskCommentDataSource[data.id] = new MatTableDataSource(
              data.risk_comments,
            );
            this.isAtRisk =
              this.studentData.at_risk_data.findIndex(
                (x) =>
                  x.demo_time_frame === this.dataService.getCurrentSemester(),
              ) !== -1;

            this.snackBarService.open('This student has been marked At-Risk');
          }

          if (dataStream.error) {
            this.snackBarService.open(dataStream.error.message, 'error');
          }
        },
      }),
    );

    this.subscription.add(
      this.dataService.getAtRiskCommentData().subscribe({
        next: (dataStream) => {
          if (dataStream.data) {
            const data = dataStream.data as RiskCommentInfo;
            this.atRiskCommentDataSource[data.at_risk_student_id].data.push(
              data,
            );
            this.atRiskCommentDataSource[data.at_risk_student_id].data = [
              ...this.atRiskCommentDataSource[data.at_risk_student_id].data,
            ];

            this.snackBarService.open(
              'Comment has been added to student that has been marked at-risk.',
            );
          }

          if (dataStream.error) {
            this.snackBarService.open(dataStream.error.message, 'error');
          }
        },
      }),
    );

    this.subscription.add(
      this.dataService.getPendingData().subscribe((pending) => {
        this.dataPending = pending;
      }),
    );

    this.subscription.add(
      this.screenSizeService.contentSize$.subscribe((size) => {
        this.contentWidth = size - 350; // 350 accoutn for the related link width
        if (
          this.group === 'default' ||
          (this.studentData && this.studentData.admit_type === 'FTIC')
        ) {
          this.setNumCardColsAndWidth(this.contentWidth);
        } else {
          this.contentWidth =
            this.contentWidth >= 580 ? 580 * 1.2 : this.contentWidth;
          this.setNumCardColsAndWidth(this.contentWidth);
        }
      }),
    );
  }

  private setNumCardColsAndWidth(size: number) {
    size *= 0.9; // 0.9 is to take into account the content width of being 90%

    if (size >= 1180) {
      this.numCardCols = 6;
      this.cardsContainerWidth = 180 * 6 + 20 * 5;
    } else if (size >= 580) {
      this.numCardCols = 3;
      this.cardsContainerWidth = 180 * 3 + 20 * 2;
    } else if (size >= 380) {
      this.numCardCols = 2;
      this.cardsContainerWidth = 180 * 2 + 20 * 1;
    } else {
      this.numCardCols = 1;
      this.cardsContainerWidth = 180 * 1 + 20;
    }
  }

  removeIdealHours(hoursTrend: HoursTrend): Partial<HoursTrend> {
    const { ideal_hours, ...rest } = hoursTrend;
    return rest;
  }

  getTakenCourses() {
    if (!this.studentData) return [];

    this.takenCoursesData = [];

    Object.values(this.studentData.taken_data).forEach(({ courses }) => {
      this.takenCoursesData.push(courses);
    });

    return this.takenCoursesData;
  }

  // reset all values saved for filter and filter
  resetFilter(input: HTMLInputElement) {
    input.value = '';
    this.filteredTakenCourse = [];
    this.showAllTable = true;
  }

  // search for course filtered by user
  filterForCourse(filterValue: Event) {
    const pattern = /[^a-zA-Z0-9]/;
    let courseCodeToSearch = (filterValue.target as HTMLInputElement).value;

    if (!courseCodeToSearch) {
      this.resetFilter(filterValue.target as HTMLInputElement);
      return;
    }

    courseCodeToSearch = courseCodeToSearch.toLowerCase();

    // check for special characters when searching for taken courses
    if (pattern.test(courseCodeToSearch)) {
      this.showAllTable = true;
      this.snackBarService.open(
        'Invalid characters in taken courses search. Please check again.',
        'error',
      );
    } else {
      this.filteredTakenCourse = [];
      this.filteredCourseCode = [];

      // Looping through taken courses key
      Object.keys(this.studentData.taken_data).forEach((key) => {
        // Loop through the array of objects
        this.studentData.taken_data[key].courses.forEach((item: any) => {
          // if taken_courses item contains courseCodeToSearch (partial values or whole)
          if (item.course_code.toLowerCase().includes(courseCodeToSearch)) {
            if (!this.filteredTakenCourse.includes(item.demo_time_frame)) {
              this.filteredTakenCourse.push(item.demo_time_frame);
            }

            if (!this.filteredCourseCode.includes(item.course_code)) {
              this.filteredCourseCode.push(item.course_code);
            }

            this.showAllTable = false;
          }
        });
      });
    }
  }

  // getting lastest semester data depending on the keys
  getValueOnStudentAttribute(terms: TermInfo[]): number | string | null {
    const [lastTerm] = terms.slice(-1);
    return lastTerm.value;
  }

  // getting lastest semester data depending on the key
  getFirstTermProgramStudentAttribute(
    terms: TermInfo[],
  ): number | string | null {
    return terms[0].value;
  }

  // check what fund type has been awarded to student
  // and save it to a variable for display it
  financialAidCheck(student: StudentTermByTerm) {
    this.financialAidGiven = [];

    if (student['award_acex'] === 'Yes') {
      this.financialAidGiven.push(' Acex');
    }

    if (student['award_pell'] === 'Yes') {
      this.financialAidGiven.push(' Pell');
    }

    if (student['award_sllc'] === 'Yes') {
      this.financialAidGiven.push(' Stem LLC');
    }

    if (student['award_bright_future'] === 'Yes') {
      this.financialAidGiven.push(' Bright Future');
    }

    if (this.financialAidGiven.length === 0) {
      this.financialAidGiven.push(' None');
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // function to toggle through different types of recommended courses table
  switchTable(event: Event) {
    event.stopPropagation();
    if (this.type === 'Good') {
      // Recommended Course (Popular)
      this.recommendedCourses = this.recommendedCoursesData.easy!;
      this.type = 'Easy';
      this.type_desc = 'Highest Average Pass Rate';
    } else if (this.type === 'Easy') {
      this.recommendedCourses = this.recommendedCoursesData.popular!;
      this.type = 'Popular';
      this.type_desc = 'Most Course Enrollment';
    } else {
      // Recommended Course (Good)
      this.recommendedCourses = this.recommendedCoursesData.good!;
      this.type = 'Good';
      this.type_desc = 'Mix of Easy/Popular';
    }
  }

  semesterName(demo_time_frame: number, cohort_year: number) {
    let z = demo_time_frame.toString();
    let year = Number(z.slice(0, 4));
    let month = Number(z.slice(4));
    let semester = '';

    if (month < 5) {
      semester = 'Spring';
    } else if (month < 8) {
      semester = 'Summer';
    } else {
      semester = 'Fall';
    }

    semester += ` ${year - cohort_year + (month >= 8 ? 1 : 0)}`;

    return semester;
  }

  getRegisteredCoursesTerm(): string {
    return `${this.semesterName(
      this.registeredCoursesData[0].demo_time_frame!,
      this.studentData.cohort,
    )} (${this.registeredCoursesData[0]
      .demo_time_frame!.toString()
      .substring(0, 4)})`;
  }

  getCourseRecommenderDemoTimeFrame() {
    let demo_time_frame = this.studentData.demo_time_frame;
    let subString = demo_time_frame.toString().slice(-2);

    if (subString === '01') {
      this.new_demo_time_frame = demo_time_frame.toString().slice(0, 4) + '05';
    }

    if (subString === '05') {
      this.new_demo_time_frame = demo_time_frame.toString().slice(0, 4) + '08';
    }

    if (subString === '08') {
      let yearStr = demo_time_frame.toString().slice(0, 4);
      let yearInt = parseInt(yearStr, 10);
      yearInt = yearInt + 1;

      this.new_demo_time_frame = yearInt.toString() + '01';
    }
  }

  getTakenCoursesTerm(demo_time_frame: number | undefined): string {
    if (typeof demo_time_frame == 'number') {
      return `${this.semesterName(
        demo_time_frame,
        this.studentData.cohort,
      )} (${demo_time_frame.toString()})`;
    } else {
      return '';
    }
  }

  getGraduationStatus() {
    return this.studentData.graduation_status === 'NoDegree'
      ? `${this.studentData.graduation_status}`
      : `${this.studentData.graduation_status} (${this.studentData.degree_term})`;
  }

  getEnrollmentStatus() {
    return this.studentData.active_status === 'Active'
      ? `${this.studentData.active_status} (${this.studentData.enroll_status})`
      : this.studentData.active_status;
  }

  // Get latest grade status for each course code and color code to show status
  getCanvasDataGradeStatus(items: CourseInfoReturn[], course_code: string) {
    let canvasStudentData = 'Not Available';
    let availableSwitch = false;
    for (let i = 0; i < items.length; i++) {
      if (items[i].course_code === course_code) {
        if (items[i].reports !== undefined) {
          items[i].reports
            ?.sort((a, b) => {
              return a.week - b.week;
            })
            .forEach((entry) => {
              availableSwitch =
                entry.grade_status != 'Not Available' ? true : availableSwitch;
              canvasStudentData =
                availableSwitch && entry.grade_status != 'Not Available'
                  ? entry.grade_status
                  : canvasStudentData;
            });
        } else {
          canvasStudentData = 'Not Available';
        }
      }
    }
    return canvasStudentData;
  }

  // function to set inner table data for registered courses
  setInnerTableDataSource(table_demo_time_frame: number | undefined) {
    if (typeof table_demo_time_frame == 'number') {
      this.canvasReportsDataSource[table_demo_time_frame] =
        new MatTableDataSource<StudentReportedCourseReturn>([]);
      this.canvasReportsDataSource[table_demo_time_frame].data = [];
      if (this.expandedElement) {
        const innerTableData: StudentReportedCourseReturn[] = [];
        for (const [
          _key,
          {
            course_code,
            course_section,
            demo_time_frame,
            enrollment_state,
            grade_score,
            grade_status,
            last_interaction_time,
            interacted_with_course,
            week,
          },
        ] of Object.entries(this.expandedElement.reports!)) {
          if (table_demo_time_frame == demo_time_frame) {
            innerTableData.push({
              course_code,
              course_section,
              demo_time_frame,
              enrollment_state,
              grade_score,
              grade_status,
              last_interaction_time: last_interaction_time
                ? new Date(last_interaction_time as string).toLocaleString()
                : 'NA',
              interacted_with_course,
              week,
            });
          }
        }
        this.canvasReportsDataSource[table_demo_time_frame].data =
          innerTableData.sort((a, b) => {
            return a.week - b.week;
          });
      }
    }
  }

  getInnerTableDataSource(table_demo_time_frame: number | undefined) {
    if (typeof table_demo_time_frame != 'number') {
      return new MatTableDataSource<StudentReportedCourseReturn>([]);
    }
    return this.canvasReportsDataSource[table_demo_time_frame];
  }

  toggleCards() {
    if (this.group === 'entry-characteristics') {
      this.group = 'default';
      this.setNumCardColsAndWidth(this.contentWidth);
    } else {
      this.group = 'entry-characteristics';
      if (this.studentData?.admit_type === 'FTIC') {
        this.setNumCardColsAndWidth(this.contentWidth);
      } else {
        const size = this.contentWidth >= 580 ? 580 * 1.2 : this.contentWidth;
        this.setNumCardColsAndWidth(size);
      }
    }
  }

  markAtRisk(event: Event) {
    event.stopPropagation();
    this._dialog.open(AtRiskStudentPopupFormComponent, {
      data: {
        uwf_id: this.studentData.uwf_id,
        selected_demo_time_frame: this.dataService.getCurrentSemester(),
        cohort: this.studentData.cohort,
        form_title: 'Mark Student At-Risk',
      },
    });
  }

  deleteAtRiskStudent(event: Event, id: number) {
    event.stopPropagation();
    const dialogRef = this._dialog.open(ConfirmDialogComponent, {
      data: {
        prompt: 'Are you sure the delete this at-risk record?',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const deletedAtRiskSemester = this.studentData.at_risk_data.find(
          (x) => x.id === id,
        );

        this.apiService.deleteAtRiskStudent(id).subscribe({
          next: (_res) => {
            this.studentData.at_risk_data =
              this.studentData.at_risk_data.filter((x) => x.id !== id);

            this.isAtRisk =
              this.studentData.at_risk_data.findIndex(
                (x) =>
                  x.demo_time_frame === this.dataService.getCurrentSemester(),
              ) !== -1;

            delete this.atRiskCommentDataSource[id];
            this.snackBarService.open(
              `At-Risk semester ${deletedAtRiskSemester?.demo_time_frame} deleted`,
            );
          },
          error: (error: HttpErrorResponse) => {
            this.snackBarService.open(
              `At-Risk semester ${deletedAtRiskSemester?.demo_time_frame} deletion failed. ${error.message}`,
              'error',
            );
          },
        });
      }
    });
  }

  addAtRiskComment(event: Event, atRiskStudentData: AtRiskStudentReturn) {
    event.stopPropagation();
    this._dialog.open(AtRiskStudentPopupFormComponent, {
      data: {
        at_risk_student_id: atRiskStudentData.id,
        selected_demo_time_frame: atRiskStudentData.demo_time_frame,
        cohort: this.studentData.cohort,
        form_title: 'Add At-Risk Comment',
      },
    });
  }

  deleteAtRiskComment(atRiskStudentId: number, commentId: number) {
    const dialogRef = this._dialog.open(ConfirmDialogComponent, {
      data: {
        prompt: 'Are you sure to delete this comment?',
      },
    });

    this.subscription.add(
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.apiService.deleteAtRiskStudentComment(commentId).subscribe({
            next: (_result) => {
              this.atRiskCommentDataSource[atRiskStudentId].data =
                this.atRiskCommentDataSource[atRiskStudentId].data.filter(
                  (x) => x.id !== commentId,
                );
              this.snackBarService.open('At-Risk comment deleted');
            },
            error: (error: HttpErrorResponse) => {
              this.snackBarService.open(
                `At-Risk comment deletion failed. ${error.message}`,
                'error',
              );
            },
          });
        }
      }),
    );
  }

  generateChartData() {
    if (this.hoursChart) {
      this.hoursChart.destroy();
    }

    setTimeout(() => {
      this.hoursChart = new Chart('hours-chart', {
        type: 'line',
        data: {
          labels: [],
          datasets: [],
        },
        options: {
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
            },
            datalabels: {
              color: '#000', // Text color
              anchor: 'center', // Position relative to bar (start, center, end)
              align: 'top', // Align text (top, middle, bottom)
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const isIdeal = context.dataset.label === 'Ideal Hours';
                  const additionalHoursLabels: string[] = [
                    isIdeal ? 'Ideal' : 'Program:',
                    `\t\tPrior Hours: ${
                      isIdeal
                        ? this.supportedHrsTrendData.ideal_hours.prior_hours
                        : this.supportedHrsTrendData.avg_program_hours
                            .prior_hours
                    }`,
                  ];

                  isIdeal
                    ? this.populateLabels(
                        additionalHoursLabels,
                        this.supportedHrsTrendData.ideal_hours,
                        context.dataIndex,
                      )
                    : this.populateLabels(
                        additionalHoursLabels,
                        this.supportedHrsTrendData.avg_program_hours,
                        context.dataIndex,
                      );

                  additionalHoursLabels.push(
                    ...[
                      'Self:',
                      `\t\tPrior Hours: ${this.supportedHrsTrendData.self_inst_hours.prior_hours}`,
                    ],
                  );

                  this.populateLabels(
                    additionalHoursLabels,
                    this.supportedHrsTrendData.self_inst_hours,
                    context.dataIndex,
                  );

                  return [
                    `${context.dataset.label}: ${context.formattedValue}`,
                    ...additionalHoursLabels,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              display: true,
              title: {
                display: true,
                text: 'Semester',
                font: {
                  size: 16,
                },
              },
            },
            y: {
              beginAtZero: true,
              display: true,
              title: {
                display: true,
                text: 'Hours',
                font: {
                  size: 16,
                },
              },
            },
          },
        },
      });

      for (const [hrsGroup, terms] of Object.entries(this.hoursTrendData)) {
        // Populate the labels only first go around
        if (!this.hoursChart.data.labels?.length) {
          this.hoursChart.data.labels = terms!.map(({ term }) => term);
        }
        this.hoursChart.data.datasets.push({
          label: this.parseLabelName(hrsGroup),
          data: terms!.map(({ value }) => value as number),
          tension: 0,
        });
      }
      this.hoursChart.update();

      this.financialAidChart && this.financialAidChart.destroy();
      this.financialAidChart = new Chart('financial-aid-chart', {
        type: 'line',
        data: {
          labels: [],
          datasets: [],
        },
        options: {
          maintainAspectRatio: false,
          plugins: {
            legend: {
              labels: {
                font: {
                  size: 15,
                },
              },
            },
            datalabels: {
              color: '#000', // Text color
              anchor: 'center', // Position relative to bar (start, center, end)
              align: 'top', // Align text (top, middle, bottom)
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              display: true,
              title: {
                display: true,
                text: 'Semester',
                font: {
                  size: 16,
                },
              },
            },
            y: {
              beginAtZero: true,
              display: true,
              title: {
                display: true,
                text: 'Amount($)',
                font: {
                  size: 16,
                },
              },
            },
          },
        },
      });
      for (const [awardName, terms] of Object.entries(
        this.studentData.financial_aid_awards!,
      )) {
        // Populate the labels only first go around
        if (!this.financialAidChart.data.labels?.length) {
          this.financialAidChart.data.labels = terms.map(({ term }) => term);
        }
        this.financialAidChart.data.datasets.push({
          label: this.parseLabelName(awardName),
          data: terms.map(({ value }) => value as number),
          tension: 0,
        });
      }
      this.financialAidChart.update();

      this.gpaComparisonChart && this.gpaComparisonChart.destroy();
      const dataServiceRef = this.dataService;
      this.gpaComparisonChart = new Chart('gpa-chart', {
        type: 'line',
        data: {
          labels: [],
          datasets: [],
        },
        options: {
          maintainAspectRatio: false,
          plugins: {
            legend: {
              labels: {
                font: {
                  size: 15,
                  family:
                    "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
                },
              },
            },
            datalabels: {
              color: '#000', // Text color
              anchor: 'center', // Position relative to bar (start, center, end)
              align: 'top', // Align text (top, middle, bottom)
              formatter(value, _context) {
                return Math.round(value * 100) / 100;
              },
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              display: true,
              title: {
                display: true,
                text: 'Semester',
                font: {
                  size: 16,
                  family:
                    "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
                },
              },
            },
            y: {
              beginAtZero: true,
              display: true,
              title: {
                display: true,
                text: 'GPA',
                font: {
                  size: 16,
                  family:
                    "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
                },
              },
            },
          },
        },
      });

      for (const [key, terms] of Object.entries(
        this.studentData.gpa_comparison!,
      )) {
        // Populate the labels on first go around and the key is not cohort.
        // Because the terms in cohort is not in the correct format. I.e: Fall 1, not Fall 1 (year)
        if (!this.gpaComparisonChart.data.labels?.length && key !== 'cohort') {
          this.gpaComparisonChart.data.labels = terms.map((term) => term.term);
        }
        this.gpaComparisonChart.data.datasets.push({
          label: this.parseLabelName(key),
          data: terms.map((term) => term.gpa),
          tension: 0,
        });
      }
      this.gpaComparisonChart.update();
    });
  }

  private populateLabels(
    labels: string[],
    data: SupportedHoursTrendTypes,
    dataIndex: number,
  ) {
    for (const [hrsType, terms] of Object.entries(data)) {
      if (hrsType === 'prior_hours') continue;
      const { value } = (terms as TermInfo[])[dataIndex];
      labels.push(`\t\t${this.parseLabelName(hrsType)}: ${value}`);
    }
  }

  private parseLabelName(str: string) {
    return str
      .split('_')
      .map((s) => {
        return s[0].toUpperCase() + s.substring(1);
      })
      .join(' ');
  }
}
